/** @jsxImportSource @emotion/react */
import React from 'react';
import { Button, Space, Tag, Typography } from 'antd';
import { PlusOutlined, StopOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../../../i18n';
import PlantingLifecycleTag from '../../../../features/plantings/components/PlantingLifecycleTag';
import LocationBreadcrumb from '../../../../features/plantings/components/LocationBreadcrumb';
import { styles } from '../styles';

const { Title, Text } = Typography;

interface Props {
  planting: any;
  currentLocation: any;
  onActionClick: () => void;
  onDispositionClick: () => void;
  projectedQuantity?: number | null;
}

const TERMINAL_STATUSES = new Set(['sold', 'dead', 'archived', 'transferred']);

const PlantingHeaderCard: React.FC<Props> = ({
  planting,
  currentLocation,
  onActionClick,
  onDispositionClick,
  projectedQuantity,
}) => {
  const { t } = useTranslation('plantings');
  return (
  <Space direction="vertical" size={8} css={{ width: '100%' }}>
    <Space css={styles.headerFlex}>
      <Space>
        <Title level={3} css={styles.noMargin}>
          {planting.crop?.name || t('header.plantingFallback')}
        </Title>
        <PlantingLifecycleTag status={planting.lifecycleStatus} />
        {planting.quantity && planting.quantity > 1 && (
          <Tag color="blue" css={styles.quantityTag}>{t('header.quantityTag', { count: planting.quantity })}</Tag>
        )}
        {projectedQuantity != null && planting.quantity > 1 && projectedQuantity < planting.quantity && (
          <Tag color="volcano" css={styles.quantityTag}>
            ⚠ {t('header.projectedQuantity', { count: projectedQuantity })}
          </Tag>
        )}
      </Space>
      <Space>
        <Button type="primary" icon={<PlusOutlined />} onClick={onActionClick}>
          {t('header.action')}
        </Button>
        {!TERMINAL_STATUSES.has(String(planting.lifecycleStatus || '')) && (
          <Button icon={<StopOutlined />} onClick={onDispositionClick}>
            {t('header.disposition')}
          </Button>
        )}
      </Space>
    </Space>

    {planting.crop?.latinName && (
      <Text type="secondary" italic>{planting.crop.latinName}</Text>
    )}

    <div css={styles.locationRow}>
      <LocationBreadcrumb
        location={currentLocation}
        locationContext={planting.locationContext}
        linkable
      />
    </div>

    {planting.quantity && planting.quantity > 1 && (
      <Text type="secondary">
        {t('header.batchPlanting', { count: planting.quantity })}
        {projectedQuantity != null && projectedQuantity < planting.quantity && (
          <Text type="danger"> · {t('header.simSurvived', { count: projectedQuantity, total: planting.quantity })}</Text>
        )}
      </Text>
    )}

    {planting.createdAt && (
      <Text type="secondary" css={styles.smallText}>
        {t('header.createdAt', { date: formatDate(planting.createdAt, 'short') })}
      </Text>
    )}
  </Space>
  );
};

export default PlantingHeaderCard;
